const ITEM_IDENTITY_FIELDS = ['section_question_id', 'question_id', 'section_id', 'main_section_id', 'id'];
const ENTITY_TYPE_NAME_FIELDS = [
  'entity_type_name',
  'entity_type_label',
  'domain_name',
  'domain',
  'entity_type',
  'name',
  'label',
];
const SECTION_NAME_FIELDS = ['section_name', 'name', 'main_section_name', 'title'];
const STATUS_FIELDS = [
  'application_status',
  'application_status_name',
  'current_status_name',
  'workflow_status_name',
  'status_name',
];
const STATUS_ID_FIELDS = [
  'current_status_id',
  'workflow_status_id',
  'status_id',
  'assigned_form_status_id',
  'application_status_id',
];
const STATUS_OBJECT_FIELDS = ['current_status', 'workflow_status', 'status'];
const TRUE_VALUE_CHECKS = [is_true_primitive, is_true_label];

function normalize_text(value) {
  return String(value || '').trim();
}

function to_number(value) {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
}

function first_logical_value(values = []) {
  return values.find(Boolean);
}

function safe_record_value(record = {}, key = '') {
  return record?.[key] ?? null;
}

function first_record_field(record = {}, fields = []) {
  return fields
    .map((field) => safe_record_value(record, field))
    .find(Boolean) || null;
}

function normalize_record_fields(record = {}, fields = []) {
  return normalize_text(first_record_field(record, fields));
}

function first_order_value(item = {}, fallback_order = 0) {
  const raw_value = [
    item?.order_number,
    item?.order,
    item?.priority,
    item?.sort_order,
    item?.question_number,
    item?.position,
    item?.sequence,
    fallback_order,
  ].find((value) => Number.isFinite(Number(value)) && Number(value) > 0);

  return Number(raw_value || fallback_order || 0);
}

function item_identity(item = {}, fallback_identity = 0) {
  return Number(item_identity_value(item, fallback_identity));
}

function item_identity_value(item = {}, fallback_identity = 0) {
  return first_logical_value([first_record_field(item, ITEM_IDENTITY_FIELDS), fallback_identity, 0]);
}

function sorted_items(items = []) {
  return [...(Array.isArray(items) ? items : [])].sort((left, right) => {
    const left_order = first_order_value(left, Number.MAX_SAFE_INTEGER);
    const right_order = first_order_value(right, Number.MAX_SAFE_INTEGER);

    if (left_order !== right_order) {
      return left_order - right_order;
    }

    return item_identity(left) - item_identity(right);
  });
}

function strict_boolean_true(value) {
  return TRUE_VALUE_CHECKS.map((check) => check(value)).some(Boolean);
}

function is_true_primitive(value) {
  return value === true || value === 1;
}

function is_true_label(value) {
  return ['true', '1', 'yes', 'required', 'mandatory'].includes(normalize_text(value).toLowerCase());
}

function question_required_value(question = {}) {
  return [
    question.is_required,
    question.isRequired,
    question.required,
    question.is_mandatory,
    question.isMandatory,
    question.mandatory,
    question.is_compulsory,
    question.isCompulsory,
    question.compulsory,
    question.question?.is_required,
    question.question?.isRequired,
    question.question?.required,
    question.question?.is_mandatory,
    question.question?.isMandatory,
    question.question?.mandatory,
    question.question?.is_compulsory,
    question.question?.isCompulsory,
    question.question?.compulsory,
  ].some(strict_boolean_true);
}

function question_key(question = {}) {
  return String(question.form_question_key || question.section_question_id || question.question_id || question.id || question.question_text || '');
}

function get_entity_type_name(entity_type = {}) {
  return normalize_record_fields(entity_type, ENTITY_TYPE_NAME_FIELDS);
}

function object_label(value = {}) {
  if (typeof value !== 'object' || value === null) return normalize_text(value);
  return normalize_text(first_logical_value([
    value?.status,
    value?.status_name,
    value?.workflow_status_name,
    value?.application_status,
    value?.entity_type_name,
    value?.name,
    value?.label,
  ]));
}

function object_status_id(value = {}) {
  if (typeof value !== 'object' || value === null) return null;

  return to_number(first_logical_value([
    value?.id,
    value?.status_id,
    value?.workflow_status_id,
    value?.assigned_form_status_id,
    value?.application_status_id,
  ]));
}

function record_domain_label(record = {}, fallback_case_type = {}) {
  return normalize_text(first_logical_value([
    record?.entity_type_name,
    record?.entity_type_label,
    record?.domain_name,
    object_label(record?.entity_type),
    record?.domain,
    fallback_case_type?.title,
  ])) || 'Case';
}

function record_status_label(record = {}) {
  return normalize_text(first_logical_value(record_status_values(record)));
}

function record_status_id(record = {}) {
  return to_number(first_logical_value([
    first_record_field(record, STATUS_ID_FIELDS),
    ...STATUS_OBJECT_FIELDS.map((field) => object_status_id(record?.[field])),
  ]));
}

function record_status_values(record = {}) {
  return [
    first_record_field(record, STATUS_FIELDS),
    ...STATUS_OBJECT_FIELDS.map((field) => object_label(record?.[field])),
  ];
}

function section_display_name(section = {}, form = {}) {
  return normalize_text(first_logical_value(section_display_values(section, form)));
}

function section_display_values(section = {}, form = {}) {
  return [first_record_field(section, SECTION_NAME_FIELDS), form?.form_name];
}

export {
  first_order_value,
  get_entity_type_name,
  item_identity,
  object_label,
  question_key,
  question_required_value,
  record_domain_label,
  record_status_id,
  record_status_label,
  section_display_name,
  sorted_items,
  strict_boolean_true,
};
